import { useEffect, useState } from 'react';
import {
  SYNC_SETTLED_EVENT,
  ENRICHMENT_RECHECK_EVENT,
} from '@/lib/sync-events';

// Shared retry-tick scaffold for the enrichment workers (offline-first
// Chunk 2, rule #4). Bumps `tick` on four triggers, all needed:
//   - `online` / `visibilitychange`-visible: re-fire so a NEVER-account
//     device retries after an offline Grok failure (no latch dependency).
//     The common mobile case is "pray offline, lock the phone, the phone
//     reconnects while backgrounded, reopen the app", where a foreground
//     `online` event is not guaranteed on iOS Safari.
//   - SYNC_SETTLED_EVENT: re-fire so a HAS-account device enriches once a
//     signed-in pull re-opens the latch.
//   - ENRICHMENT_RECHECK_EVENT: re-fire the moment accountKnown() resolves at
//     boot (authResolved + bootSeedDone), so a never-account device's
//     boot-deferred rows enrich without waiting for an incidental trigger
//     (codex pass-16).
// Correctness is enforced by the per-device rule the workers apply, NOT by the
// trigger source, so a broad trigger is safe.
export function useReconnectRetry(): { tick: number } {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    const onVisible = () => {
      if (navigator.onLine && document.visibilityState === 'visible') bump();
    };
    window.addEventListener('online', bump);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(SYNC_SETTLED_EVENT, bump);
    window.addEventListener(ENRICHMENT_RECHECK_EVENT, bump);
    return () => {
      window.removeEventListener('online', bump);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(SYNC_SETTLED_EVENT, bump);
      window.removeEventListener(ENRICHMENT_RECHECK_EVENT, bump);
    };
  }, []);
  return { tick };
}
